import type { V2CustomerBillBillStatisticsPostResponse } from '@maxtropy/device-customer-apis-v2';
import type { BarSeriesOption } from 'echarts';
import { colors, TimeType } from './const';
import { isNil } from 'lodash-es';

export interface GetEchartsOptionParams {
  chartData?: V2CustomerBillBillStatisticsPostResponse;
  timeType: TimeType;
}

const parseNumber = (value: unknown) => {
  if (isNil(value) || value === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const shouldConvertToTenThousand = (values: unknown[]) => {
  const validValues = values.map(parseNumber).filter((value): value is number => !isNil(value));
  return validValues.length > 0 && validValues.every(value => value >= 10000);
};

const getStableColor = (key: string | number) => {
  const text = String(key);
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % colors.length;
  }
  return colors[hash];
};

const getMediumColor = (chartData: V2CustomerBillBillStatisticsPostResponse, mediumName: string) => {
  const detail = chartData.chartList
    ?.flatMap(item => item.detailList ?? [])
    .find(detail => detail.mediumName === mediumName && !isNil(detail.mediumId));

  return getStableColor(detail?.mediumId ?? mediumName);
};

export const getEchartsOption = (params: GetEchartsOptionParams) => {
  const { chartData, timeType } = params;
  const isYear = timeType === TimeType.year;
  if (!chartData?.chartList?.length) return {};

  // 获取所有能源介质名称（去重，最多12条）
  const mediumNames = new Set<string>();
  chartData.chartList.forEach(item => {
    item.detailList?.forEach(detail => {
      if (detail.mediumName) {
        mediumNames.add(detail.mediumName);
      }
    });
  });
  const mediumList = Array.from(mediumNames).slice(0, 12);

  const costValues = chartData.chartList.flatMap(item => item.detailList?.map(detail => detail.cost) ?? []);
  const shouldConvertCost = shouldConvertToTenThousand(costValues);

  // 单位和转换系数
  const unit = shouldConvertCost ? '万元' : '元';
  const divisor = shouldConvertCost ? 10000 : 1;

  // x 轴数据
  const xAxisData = chartData.chartList.map(item => item.time || '');

  // 根据时间类型和图例数量动态计算柱状图宽度和间距
  // 百分比参照：每个类目（category）的宽度
  const mediumCount = mediumList.length;

  // 先确定barGap（柱子间距相对于柱子宽度的百分比）
  let barGap: string;
  if (isYear) {
    barGap = '50%'; // 按年：间距大一些
  } else {
    barGap = '40%'; // 按月：间距小一些
  }

  // 根据图例数量动态计算barWidth
  // 计算公式：总宽度 = n × barWidth + (n-1) × barWidth × barGap%
  // 假设总宽度占类目宽度的80%（留一些边距），求解barWidth
  const gapRatio = parseFloat(barGap) / 100;
  const totalRatio = 0.8; // 柱子总宽度占类目宽度的80%
  // totalRatio = barWidthRatio × (mediumCount + (mediumCount - 1) × gapRatio)
  // barWidthRatio = totalRatio / (mediumCount + (mediumCount - 1) × gapRatio)
  const barWidthRatio = totalRatio / (mediumCount + (mediumCount - 1) * gapRatio);
  const barWidth = `${(barWidthRatio * 100).toFixed(1)}%`;

  // 系列数据：每个能源介质一个系列
  const series: BarSeriesOption[] = mediumList.map(mediumName => ({
    name: mediumName,
    type: 'bar',
    barWidth,
    barGap,
    itemStyle: {
      color: getMediumColor(chartData, mediumName),
    },
    data: chartData?.chartList?.map(item => {
      const detail = item.detailList?.find(d => d.mediumName === mediumName);
      const cost = parseNumber(detail?.cost);
      return isNil(cost) ? null : cost / divisor;
    }),
  }));

  return {
    backgroundColor: 'transparent',
    color: colors,
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(0,0,0,0.8)',
      borderColor: 'transparent',
      extraCssText: 'border-radius: 2px;',
      textStyle: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 12,
        fontWeight: 500,
      },
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: any[]) => {
        const timeIndex = chartData?.chartList?.findIndex(item => item.time === params[0].axisValue);
        const currentTotalCostRaw = chartData?.chartList?.[timeIndex!]?.totalCost;
        const currentTotalCost = parseNumber(currentTotalCostRaw);
        const total = !isNil(currentTotalCost) ? currentTotalCost / divisor : null;
        // 表格样式
        let html = `
          <div style="margin-bottom: 8px;">${params[0].axisValue}</div>
          <table style="border-collapse: collapse; width: 100%;">
            <tr>
              <td style="padding: 2px 16px 2px 0;">总成本</td>
              <td style="padding: 2px 16px;">${isNil(total) ? '-' : `${total.toFixed(2)} ${unit}`}</td>
              <td style="padding: 2px 0;"></td>
            </tr>
        `;

        params.forEach(param => {
          // 获取对应时间点的占比
          const detail = chartData?.chartList?.[timeIndex!]?.detailList?.find(d => d.mediumName === param.seriesName);
          const proportion = !isNil(detail?.proportion) ? `${detail.proportion}%` : '-';
          const valueDisplay = isNil(param.value) ? '-' : `${param.value.toFixed(2)} ${unit}`;

          html += `
            <tr>
              <td style="padding: 2px 16px 2px 0;">${param.marker} ${param.seriesName}</td>
              <td style="padding: 2px 16px;">${valueDisplay}</td>
              <td style="padding: 2px 0 2px 16px;">${proportion}</td>
            </tr>
          `;
        });

        html += '</table>';
        return html;
      },
    },
    legend: {
      type: 'scroll',
      right: 0,
      top: 0,
      icon: 'square',
      itemWidth: 12,
      itemHeight: 12,
    },
    grid: {
      left: '8px',
      right: '40px',
      bottom: '3%',
      top: 60,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: xAxisData,
    },
    yAxis: {
      type: 'value',
      name: `${unit}`,
    },
    series,
  };
};

export const getMediumEchartsOption = (params: GetEchartsOptionParams) => {
  const { chartData } = params;
  if (!chartData?.chartList?.length) return [];

  // 获取所有能源介质名称（去重，保持顺序，最多 12 条）- 与 getEchartsOption 保持一致
  const mediumNames = new Set<string>();
  chartData.chartList.forEach(item => {
    item.detailList?.forEach(detail => {
      if (detail.mediumName) {
        mediumNames.add(detail.mediumName);
      }
    });
  });
  const mediumList = Array.from(mediumNames).slice(0, 12);

  const usageConfigMap = new Map<string, { unit: string; divisor: number }>();
  mediumList.forEach(mediumName => {
    const details = chartData.chartList?.map(item => item.detailList?.find(d => d.mediumName === mediumName));
    const usageValues = (details ?? []).map(detail => detail?.usage);
    const shouldConvertUsage = shouldConvertToTenThousand(usageValues);
    const baseUnit = (details ?? []).find(detail => detail?.physicalUnitName)?.physicalUnitName || '';
    usageConfigMap.set(mediumName, {
      unit: shouldConvertUsage && baseUnit ? `万${baseUnit}` : baseUnit,
      divisor: shouldConvertUsage ? 10000 : 1,
    });
  });

  // 收集介质单位
  const mediumUnits = new Map<string, string>();
  chartData.chartList.forEach(item => {
    item.detailList?.forEach(detail => {
      if (detail.mediumName && detail.physicalUnitName) {
        mediumUnits.set(detail.mediumName, usageConfigMap.get(detail.mediumName)?.unit || detail.physicalUnitName);
      }
    });
  });

  return mediumList.map(mediumName => {
    const unit = mediumUnits.get(mediumName) || '';
    const barColor = getMediumColor(chartData, mediumName);

    // 提取该介质在各时间点用量数据
    const seriesData = chartData.chartList?.map(item => {
      const detail = item.detailList?.find(d => d.mediumName === mediumName);
      const usage = parseNumber(detail?.usage);
      const divisor = usageConfigMap.get(mediumName)?.divisor ?? 1;
      return !isNil(usage) && usage >= 0 ? usage / divisor : null;
    });

    return {
      mediumName,
      color: barColor,
      option: {
        backgroundColor: 'transparent',
        color: [barColor],
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0,0,0,0.8)',
          borderColor: 'transparent',
          extraCssText: 'border-radius: 2px;',
          textStyle: {
            color: 'rgba(255,255,255,0.8)',
            fontSize: 12,
            fontWeight: 500,
          },
          formatter: (params: any[]) => {
            const value = params[0].value;
            const valueDisplay = isNil(value) ? '-' : `${value.toFixed(2)} ${unit}`;
            return `${params[0].axisValue}<br/>${params[0].marker} ${mediumName}: ${valueDisplay}`;
          },
        },
        grid: {
          left: '40px',
          right: '40px',
          bottom: '3%',
          top: 60,
          containLabel: true,
        },
        legend: {
          type: 'scroll',
          right: 0,
          top: 0,
          icon: 'square',
          itemWidth: 12,
          itemHeight: 12,
        },
        xAxis: {
          type: 'category',
          data: chartData.chartList?.map(item => item.time || ''),
        },
        yAxis: {
          type: 'value',
          name: `${mediumName}(${unit})`,
        },
        series: [
          {
            name: mediumName,
            type: 'bar',
            barWidth: '50%',
            data: seriesData,
            itemStyle: {
              color: barColor,
              borderRadius: [4, 4, 0, 0],
            },
          },
        ],
      },
    };
  });
};
